import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { useParams } from 'react-router-dom';
import { Loader2, Calendar as CalendarIcon } from 'lucide-react';
import { api } from '@/lib/api';
import { Empty } from '@/components/Empty';
import { cn } from '@/lib/cn';

const PILIER_DOTS: Record<string, string> = {
  paja: 'bg-yellow-400',
  livre: 'bg-blue-400',
  ecole: 'bg-emerald-400',
  coulisses: 'bg-purple-400',
  guide: 'bg-rose-400',
};

const PLATFORM_LABELS: Record<string, string> = {
  instagram: 'IG',
  tiktok: 'TT',
  youtube_shorts: 'YT',
  facebook: 'FB',
  threads: 'TH',
  pinterest: 'PI',
  linkedin: 'LI',
  x: 'X',
};

export function CalendarPage() {
  const { slug } = useParams<{ slug: string }>();
  const { getToken } = useAuth();

  const calendarQuery = useQuery({
    queryKey: ['calendar', slug],
    queryFn: () => api.listCalendar(slug!, () => getToken()),
    refetchInterval: 60_000,
  });

  // Group by date
  const byDate = calendarQuery.data?.reduce<Record<string, typeof calendarQuery.data>>((acc, item) => {
    if (!acc[item.date]) acc[item.date] = [];
    acc[item.date]!.push(item);
    return acc;
  }, {});

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold leading-tight">Calendrier de publication</h1>
        <p className="text-sm text-ducens-muted mt-1">
          50 scripts × 8 plateformes sur 8 semaines.
        </p>
      </div>

      {calendarQuery.isPending && (
        <div className="flex items-center gap-2 text-ducens-muted">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
        </div>
      )}

      {calendarQuery.data?.length === 0 && (
        <Empty Icon={CalendarIcon} title="Calendrier vide" description="Aucune publication planifiée." />
      )}

      {byDate && (
        <>
          <Legend />
          <div className="space-y-2">
            {Object.entries(byDate)
              .sort(([a], [b]) => a.localeCompare(b))
              .slice(0, 30)
              .map(([date, items]) => (
                <DayRow key={date} date={date} items={items} />
              ))}
          </div>
          {Object.keys(byDate).length > 30 && (
            <div className="text-xs text-ducens-muted text-center mt-4">
              + {Object.keys(byDate).length - 30} jours supplémentaires
            </div>
          )}
        </>
      )}
    </>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-ducens-muted">
      {Object.entries(PILIER_DOTS).map(([pilier, color]) => (
        <span key={pilier} className="inline-flex items-center gap-1.5">
          <span className={cn('w-2 h-2 rounded-full', color)} />
          {pilier}
        </span>
      ))}
    </div>
  );
}

type Item = { date: string; platform: string; pilier: string; title: string; status: 'pending' | 'published' | 'failed' };

function DayRow({ date, items }: { date: string; items: Item[] }) {
  const d = new Date(date);
  const formatted = d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' });
  const titles = Array.from(new Set(items.map((i) => i.title)));

  return (
    <div className="bg-white border border-ducens-border rounded-md px-4 py-3 flex items-start gap-4 text-sm">
      <div className="w-32 shrink-0">
        <div className="font-medium capitalize">{formatted}</div>
        <div className="text-xs text-ducens-muted mt-0.5">{items.length} posts</div>
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        {titles.map((title) => {
          const t = items.find((i) => i.title === title)!;
          return (
            <div key={title} className="flex items-center gap-2">
              <span className={cn('w-2 h-2 rounded-full shrink-0', PILIER_DOTS[t.pilier] ?? 'bg-gray-400')} />
              <span className="truncate flex-1">{title}</span>
              <div className="hidden sm:flex flex-wrap gap-1 shrink-0">
                {items
                  .filter((i) => i.title === title)
                  .map((i, idx) => (
                    <span
                      key={idx}
                      className={cn(
                        'inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium',
                        i.status === 'published' && 'bg-emerald-100 text-emerald-900',
                        i.status === 'pending' && 'bg-ducens-bg text-ducens-muted',
                        i.status === 'failed' && 'bg-red-100 text-red-900'
                      )}
                    >
                      {PLATFORM_LABELS[i.platform] ?? i.platform}
                    </span>
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
